import { supabase } from '../../../services/supabaseClient';

/**
 * Platform Config - fetch MQTT/HTTP settings from the platform_config table.
 *
 * Server-side: getPlatformConfig(supabaseClient, 'mqtt')
 * Client-side: fetchPlatformConfig('mqtt')  (calls the API route)
 */

// ─── Server-side (use inside API routes / server components) ─────────────────

export async function getPlatformConfig(supabase, key) {
  const { data, error } = await supabase
    .from('platform_config')
    .select('config_value')
    .eq('config_key', key)
    .single()

  if (error || !data) {
    console.error(`Failed to load platform config "${key}":`, error?.message)
    return null
  }

  return data.config_value
}

export async function getAllPlatformConfigs(supabase) {
  const { data, error } = await supabase
    .from('platform_config')
    .select('config_key, config_value')

  if (error || !data) {
    console.error('Failed to load platform configs:', error?.message)
    return {}
  }

  const configs = {}
  data.forEach(row => { configs[row.config_key] = row.config_value })
  return configs
}

// ─── Client-side (use inside React components) ───────────────────────────────

let clientCache = {}
let cacheTimestamp = 0
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export async function fetchPlatformConfig(key) {
  const now = Date.now()
  if (clientCache[key] && (now - cacheTimestamp) < CACHE_TTL_MS) {
    return clientCache[key]
  }

  try {
    const query = supabase.from('platform_config').select('config_key, config_value')
    const { data, error } = key
      ? await supabase.from('platform_config').select('config_value').eq('config_key', key).maybeSingle()
      : await query

    if (error || !data) return null

    if (key) {
      clientCache[key] = data.config_value
      cacheTimestamp = now
      return data.config_value
    }

    const configs = {}
    ;(data || []).forEach((row) => {
      configs[row.config_key] = row.config_value
      clientCache[row.config_key] = row.config_value
    })
    cacheTimestamp = now
    return configs
  } catch (err) {
    console.error('Failed to fetch platform config:', err)
    return null
  }
}

export function invalidatePlatformConfigCache() {
  clientCache = {}
  cacheTimestamp = 0
}
